import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { USE_CASES, LanguageContent } from '../data/useCases';
import SeoHead from '../components/SeoHead';
import { BreadcrumbSchema } from '../components/JsonLd';

const UseCaseDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = t('common.lang', { defaultValue: 'fr' }) as keyof LanguageContent;

  const uc = USE_CASES.find((u) => u.slug === slug);

  if (!uc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ paddingTop: 80 }}>
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)', marginBottom: 24 }}>
          {lang === 'fr' ? 'Cas d\'usage introuvable.' : 'Use case not found.'}
        </p>
        <button className="btn-primary" onClick={() => navigate('/use-cases')}>
          {lang === 'fr' ? 'Retour aux cas d\'usage' : 'Back to use cases'}
        </button>
      </div>
    );
  }

  const siblingCases = USE_CASES.filter((u) => u.slug !== slug);

  const seoTitle = lang === 'fr'
    ? `${uc.company} \u00d7 Blue Owl SAS | ${uc.title.fr}`
    : `${uc.company} \u00d7 Blue Owl SAS | ${uc.title.en}`;
  const seoDesc = lang === 'fr' ? uc.description.fr : uc.description.en;

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        canonical={`/use-cases/${uc.slug}`}
        ogImage={uc.logo.startsWith('/') ? `https://blueowl.fr${uc.logo}` : uc.logo}
      />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://blueowl.fr/' },
        { name: lang === 'fr' ? "Cas d'usages" : 'Use Cases', url: 'https://blueowl.fr/use-cases' },
        { name: uc.company, url: `https://blueowl.fr/use-cases/${uc.slug}` },
      ]} />
    <div className="max-w-4xl mx-auto px-6 py-8" style={{ paddingTop: 96 }}>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 mb-10 text-sm" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)' }}>
        <button
          onClick={() => navigate('/')}
          className="transition-colors hover:text-primary"
          style={{ color: 'var(--color-text-secondary)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-primary)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text-secondary)')}
        >
          Accueil
        </button>
        <span style={{ opacity: 0.4 }}>›</span>
        <Link
          to="/use-cases"
          style={{ color: 'var(--color-text-secondary)', textDecoration: 'none' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-primary)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text-secondary)')}
        >
          {lang === 'fr' ? "Cas d'usages" : 'Use Cases'}
        </Link>
        <span style={{ opacity: 0.4 }}>›</span>
        <span style={{ color: 'var(--color-primary)' }}>{uc.company}</span>
      </nav>

      {/* Hero card */}
      <div className="card-glass overflow-hidden mb-8" style={{ borderRadius: 'var(--radius-2xl)' }}>
        {/* Logo banner */}
        <div
          className="flex items-center justify-between py-10 px-8"
          style={{
            background: 'linear-gradient(135deg, rgba(54,109,198,0.06) 0%, rgba(10,10,15,0) 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <img
            src={uc.logo}
            alt={lang === 'fr'
              ? `Logo ${uc.company} — cas d'usage IoT Blue Owl SAS`
              : `${uc.company} logo — IoT use case Blue Owl SAS`}
            width={280}
            height={72}
            style={{
              maxHeight: 72,
              maxWidth: 280,
              objectFit: 'contain',
              ...(uc.logoFilter !== false
                ? { filter: 'brightness(0) invert(1)', opacity: 0.85 }
                : { opacity: 1 }),
            }}
          />
          <span
            className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{
              background: `${uc.sectorColor}18`,
              color: uc.sectorColor,
              border: `1px solid ${uc.sectorColor}40`,
              fontFamily: 'var(--font-body)',
              whiteSpace: 'nowrap',
            }}
          >
            {uc.sector[lang]}
          </span>
        </div>

        {/* Main content */}
        <div className="p-8 md:p-10">
          {/* Sector (mobile) + Tech badges */}
          <div className="flex flex-wrap gap-2 mb-5">
            <span
              className="sm:hidden inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
              style={{
                background: `${uc.sectorColor}18`,
                color: uc.sectorColor,
                border: `1px solid ${uc.sectorColor}40`,
                fontFamily: 'var(--font-body)',
              }}
            >
              {uc.sector[lang]}
            </span>
            {uc.technologies.map((tech) => (
              <span key={tech} className="badge badge-primary">{tech}</span>
            ))}
          </div>

          <h1
            className="mb-4"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
              color: 'var(--color-text-primary)',
              lineHeight: 1.2,
            }}
          >
            {uc.title[lang]}
          </h1>

          <p
            className="mb-8"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text-secondary)',
            }}
          >
            {uc.description[lang]}
          </p>

          {/* Challenge / Context */}
          <div
            className="mb-8 p-6 rounded-xl"
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderLeft: `3px solid ${uc.sectorColor}`,
            }}
          >
            <h2
              className="mb-3 flex items-center gap-2"
              style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '1rem', color: uc.sectorColor }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zm0 4v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
              </svg>
              {lang === 'fr' ? 'Le défi' : 'The Challenge'}
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', lineHeight: 1.7, color: 'var(--color-text-secondary)' }}>
              {uc.context[lang]}
            </p>
          </div>

          {/* Key results */}
          <h2
            className="mb-4"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '1rem', color: 'var(--color-text-primary)' }}
          >
            {lang === 'fr' ? 'Résultats clés' : 'Key Results'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {uc.results.map((r, i) => (
              <div
                key={i}
                className="text-center py-5 px-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)' }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    color: uc.sectorColor,
                    lineHeight: 1.1,
                    marginBottom: 6,
                  }}
                >
                  {r.value}
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                  {r.label[lang]}
                </div>
              </div>
            ))}
          </div>

          {/* Key highlights */}
          <h2
            className="mb-4"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: '1rem',
              color: 'var(--color-text-primary)',
            }}
          >
            {lang === 'fr' ? 'Points clés' : 'Key Highlights'}
          </h2>
          <ul className="space-y-3">
            {uc.highlights.map((hl, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full"
                  style={{ width: 22, height: 22, background: `${uc.sectorColor}18`, color: uc.sectorColor }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  {hl[lang]}
                </span>
              </li>
            ))}
          </ul>

          {/* Duration pill */}
          <div className="mt-6 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="5.5" stroke="var(--color-text-muted)" strokeWidth="1.5"/>
              <path d="M7 4.5V7l2 1.5" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
              {uc.duration[lang]}
              {uc.ongoing && (
                <span
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(54,109,198,0.12)', color: '#366DC6' }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#366DC6', display: 'inline-block' }} />
                  {lang === 'fr' ? 'En cours' : 'Ongoing'}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div
        className="card-glass text-center py-10 px-8 mb-14"
        style={{ borderRadius: 'var(--radius-2xl)' }}
      >
        <h3
          className="mb-3"
          style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.375rem', color: 'var(--color-text-primary)' }}
        >
          {lang === 'fr' ? 'Un projet similaire en tête ?' : 'Have a similar project in mind?'}
        </h3>
        <p className="mb-6" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-secondary)' }}>
          {lang === 'fr'
            ? 'Nous sommes là pour vous aider à concrétiser votre vision.'
            : "We're here to help you bring your vision to life."}
        </p>
        <button className="btn-primary" onClick={() => { navigate('/'); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300); }}>
          {lang === 'fr' ? 'Discutons-en' : "Let's Talk"}
        </button>
      </div>

      {/* Other use cases */}
      {siblingCases.length > 0 && (
        <section>
          <h2
            className="mb-6"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.375rem', color: 'var(--color-text-primary)' }}
          >
            {lang === 'fr' ? "Autres cas d'usages" : 'Other use cases'}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {siblingCases.map((s) => (
              <button
                key={s.slug}
                onClick={() => navigate(`/use-cases/${s.slug}`)}
                className="card-glass text-left flex items-center gap-5 p-5 transition-all"
                style={{ borderRadius: 'var(--radius-xl)', cursor: 'pointer', border: '1px solid transparent' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = `${s.sectorColor}50`)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'transparent')}
              >
                <img
                  src={s.logo}
                  alt={lang === 'fr'
                    ? `Logo ${s.company} — cas d'usage Blue Owl SAS`
                    : `${s.company} logo — Blue Owl SAS use case`}
                  width={90}
                  height={36}
                  style={{
                    height: 36,
                    maxWidth: 90,
                    objectFit: 'contain',
                    flexShrink: 0,
                    ...(s.logoFilter !== false
                      ? { filter: 'brightness(0) invert(1)', opacity: 0.7 }
                      : { opacity: 0.9 }),
                  }}
                />
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '0.9375rem', color: s.sectorColor }}>
                    {s.company}
                  </div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    {s.title[lang]}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
    </>
  );
};

export default UseCaseDetail;
